import React, { useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { RUN_KIND, RUN_STATUS } from '../../features/stockSync/stockSyncConstants';
import {
  catalogResultLines,
  formatSyncDate,
  previewSummaryLines,
  publishResultLines,
  runHeadline,
  skippedOf,
} from '../../features/stockSync/stockSyncUtils';
import { formatQuantity } from '../../utils/inventoryLabels';

const TEXTS = {
  [RUN_KIND.CATALOG]: {
    title: 'Última sincronización',
    empty: 'Todavía no se sincronizan los SKUs de esta tienda.',
    skipped: (count) => (count === 1 ? 'variante omitida' : 'variantes omitidas'),
  },
  [RUN_KIND.STOCK_PREVIEW]: {
    title: 'Última comparación',
    empty: 'Todavía no se compara el stock de esta tienda.',
  },
  [RUN_KIND.PUBLISH]: {
    title: 'Última publicación',
    empty: 'Todavía no se publica el stock en esta tienda.',
  },
};

const RESULT_LINES = {
  [RUN_KIND.CATALOG]: catalogResultLines,
  [RUN_KIND.STOCK_PREVIEW]: (result) => previewSummaryLines(result?.summary),
  [RUN_KIND.PUBLISH]: publishResultLines,
};

const HEADLINE_STYLE = {
  success: { icon: CheckCircle2, className: 'text-ninja-green' },
  danger: { icon: XCircle, className: 'text-danger' },
  info: { icon: Loader2, className: 'text-info', spin: true },
};

const WARNING_TEXT = 'flex items-center gap-1.5 font-semibold text-amber-700 dark:text-amber-300';
const LINE_STYLE = { warning: WARNING_TEXT, danger: 'font-semibold text-danger' };
const LIST = 'max-h-56 overflow-y-auto divide-y divide-border rounded-xl border border-border';
const LIST_ROW = 'grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)] gap-x-3 px-3 py-2';

/** A count with a warning sign, and the list behind it on demand. */
const Expandable = ({ label, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={WARNING_TEXT}>
          <AlertTriangle size={16} aria-hidden="true" className="shrink-0" />
          {label}
        </span>
        <Button
          variant="subtle"
          size="sm"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          data-testid={testId}
        >
          {open ? 'Ocultar' : 'Ver cuáles'}
        </Button>
      </div>
      {open && <ul className={LIST}>{children}</ul>}
    </div>
  );
};

/**
 * How a store's last catalog sync, comparison or publication went: where it
 * stands, the numbers it left once done, and what was left out and why.
 *
 * @param {object} props
 * @param {'catalog'|'activation_preview'|'publish'} props.kind
 * @param {object|null} props.report - As the server keeps it; `null` when there is none.
 * @param {React.ReactNode} [props.footer] - What the page adds once it is done, as a link to go on.
 */
export const ShopifyRunSummary = ({ kind, report, footer = null }) => {
  const texts = TEXTS[kind];
  const headline = runHeadline(kind, report);
  const style = headline ? HEADLINE_STYLE[headline.tone] : null;
  const HeadlineIcon = style?.icon;
  const isDone = report?.status === RUN_STATUS.DONE;
  const lines = isDone ? RESULT_LINES[kind](report.result) : [];
  const skipped = isDone && texts.skipped ? skippedOf(report.result) : { total: 0, rows: [] };
  // The nightly publication says so from the moment it is queued.
  const nightly = report?.nightly || report?.result?.nightly;

  return (
    <section
      data-testid="shopify-run"
      data-kind={kind}
      data-status={report?.status ?? 'none'}
      className="rounded-2xl border border-border p-4 space-y-2 text-sm text-ninja-dark dark:text-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold">
          {texts.title}
          {nightly && <span className="font-normal text-text-muted"> · nocturna</span>}
        </h3>
        {headline && (
          <span className={clsx('flex items-center gap-1.5 font-semibold', style.className)}>
            <HeadlineIcon size={16} aria-hidden="true" className={clsx('shrink-0', style.spin && 'animate-spin')} />
            {headline.text}
            {headline.at && <span className="font-normal text-text-muted">· {formatSyncDate(headline.at)}</span>}
          </span>
        )}
      </div>

      {!report && <p className="text-text-muted">{texts.empty}</p>}
      {report?.status === RUN_STATUS.FAILED && (
        <p data-testid="shopify-run-error" className="text-danger">
          {report.error}
        </p>
      )}

      {lines.map((line) => (
        <p key={line.key} data-testid="shopify-run-line" data-line={line.key} className={clsx(LINE_STYLE[line.tone])}>
          {line.tone === 'warning' && <AlertTriangle size={16} aria-hidden="true" className="shrink-0" />}
          {line.text}
        </p>
      ))}

      {skipped.total > 0 && (
        <Expandable label={`${formatQuantity(skipped.total)} ${texts.skipped(skipped.total)}`} testId="shopify-skipped-toggle">
          {skipped.rows.map((row, index) => (
            <li
              // The same SKU can be left out twice: a repeated SKU is the reason.
              key={`${row.sku}-${index}`}
              data-testid="shopify-skipped-row"
              data-reason={row.reason}
              className={LIST_ROW}
            >
              <span className="font-mono font-semibold truncate" title={row.sku || undefined}>
                {row.sku || '—'}
              </span>
              <span className="truncate" title={row.title}>
                {row.title}
              </span>
              <span className="col-start-2 text-xs text-text-muted">{row.reason_label}</span>
            </li>
          ))}
          {skipped.total > skipped.rows.length && (
            <li className="px-3 py-2 text-xs text-text-muted">
              Y {formatQuantity(skipped.total - skipped.rows.length)} más: el reporte guarda los primeros{' '}
              {formatQuantity(skipped.rows.length)}.
            </li>
          )}
        </Expandable>
      )}

      {footer}
    </section>
  );
};
